import { FarmMedicalSupplyViewModel, MedicalSupplyType } from '../models/injection';
import {
  Alert,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  type SelectChangeEvent,
  type SxProps,
  type Theme
} from '@mui/material';
import { useTranslation } from 'react-i18next';

interface FarmMedicalSupplySelectProps {
  supplies: FarmMedicalSupplyViewModel[];
  value: string;
  onSupplyChange: (value: string) => void;
  /** Extra styles for the FormControl. */
  sx?: SxProps<Theme>;
  fullWidth?: boolean;
  allowAny?: boolean;
  disabled?: boolean;
  selectId?: string;
  labelText?: string;
}

// The 48px band v4 gave these section headers, with the label centred in it.
const SECTION_ALERT_SX = { py: 0, minHeight: 48, alignItems: 'center' } as const;

const FarmMedicalSupplySelect = ({
  supplies,
  value,
  onSupplyChange,
  sx,
  fullWidth,
  allowAny,
  disabled,
  selectId,
  labelText
}: FarmMedicalSupplySelectProps) => {
  const { t } = useTranslation();

  const handleSupplyChange = (event: SelectChangeEvent<string>) => {
    const supplyValue = event.target.value;

    //callback
    onSupplyChange && onSupplyChange(supplyValue);
  };

  const filterSupplies = (supplyType: MedicalSupplyType) =>
    supplies.filter((x) => x.supplyType === supplyType).sort((a, b) => a.number.localeCompare(b.number));

  return (
    <FormControl sx={sx} disabled={disabled} fullWidth={fullWidth} variant="outlined" size="small">
      <InputLabel id="farmMedicalSupplyLabel">{labelText || t('f-supp-farm')}</InputLabel>
      <Select
        // Must match the InputLabel: the outlined variant sizes the notch in the border from this, so a custom
        // label with the default here would overlap the outline once it shrinks.
        label={labelText || t('f-supp-farm')}
        labelId="farmMedicalSupplyLabel"
        id={selectId || 'farmMedicalSupplySelect'}
        value={value}
        onChange={handleSupplyChange}
      >
        {allowAny && (
          <MenuItem key={'any'} value={'any'}>
            {t('f-any')}
          </MenuItem>
        )}

        <ListSubheader sx={{ px: 0 }}>
          <Alert severity="info" sx={SECTION_ALERT_SX}>
            {t('f-supp-vaccination')}
          </Alert>
        </ListSubheader>

        {filterSupplies(MedicalSupplyType.Vaccination).map((supply) => (
          <MenuItem key={supply.id} value={supply.id}>
            {supply.number}
          </MenuItem>
        ))}

        <ListSubheader sx={{ px: 0 }}>
          <Alert severity="info" sx={SECTION_ALERT_SX}>
            {t('f-supp-antibiotic')}
          </Alert>
        </ListSubheader>

        {filterSupplies(MedicalSupplyType.Antibiotic).map((supply) => (
          <MenuItem key={supply.id} value={supply.id}>
            {supply.number}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default FarmMedicalSupplySelect;
